import React, { useContext, useEffect, useState, useRef } from 'react';
import axios from "axios";
import { Link, navigate, useQueryParams } from 'raviger';
import { Button, Card, CardGroup, Col, Collapse, Form, FormControl, InputGroup, ListGroup, OverlayTrigger, Row, Spinner, Tooltip } from 'react-bootstrap';
import moment from 'moment';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBan, faCar, faChevronDown, faChevronUp, faDownload, faEquals, faEnvelope, faInfoCircle, faKey, faTrailer, faFilePdf
} from '@fortawesome/free-solid-svg-icons';
import {
  faDotCircle
} from '@fortawesome/free-regular-svg-icons';
import { faChevronDoubleDown, faChevronDoubleUp, faCommentSmile, faPhoneRotary } from '@fortawesome/pro-solid-svg-icons';
import Moment from 'react-moment';
import { DATE_FORMAT } from '../constants';
import { AuthContext } from "../accounts/AccountsReducer";
import { useMark, useSubmitting } from '../hooks';
import Header from '../components/Header';
import Scrollbar from '../components/Scrollbars';
import Paginator from '../components/Paginator';
import { ITEMS_PER_PAGE } from '../constants';
import { SystemErrorContext } from '../components/SystemError';
import { DateRangePicker } from '../components/Form';
import { printAllServiceRequests } from './Utils';
import { titleCase } from '../components/Utils';
import LoadingLink from "../components/LoadingLink";
import ActionsDropdown from '../components/ActionsDropdown';

function ServiceRequestSearch({ incident, organization }) {

  const { setShowSystemError } = useContext(SystemErrorContext);

  // Identify any query param data.
  const [queryParams] = useQueryParams();
  const {
    search = '',
    status = '',
  } = queryParams;

  const priorityText = {1:'Urgent', 2:'High', 3:'Medium', 4:'Low', 5:'Lowest'};

  const customStyles = {
    // For the select it self, not the options of the select
    control: (styles, { isDisabled}) => {
      return {
        ...styles,
        color: '#FFF',
        cursor: isDisabled ? 'not-allowed' : 'default',
        backgroundColor: isDisabled ? '#DFDDDD' : 'white',
        height: 35,
        minHeight: 35,
        marginBottom: "15px"
      }
    },
    option: provided => ({
      ...provided,
      color: 'black'
    }),
  };

  const [data, setData] = useState({service_requests: [], total_count:0, isFetching: false});
  const [searchState, setSearchState] = useState({});
  const [triggerRefresh, setTriggerRefresh] = useState(false);
  const [searchTerm, setSearchTerm] = useState(search);
  const [isLoading, setIsLoading] = useState(false);
  const tempSearchTerm = useRef(null);
  const priorityRef = useRef(null);
  const statusRef = useRef(null);
  const openStartRef = useRef(null);
  const openEndRef = useRef(null);
  const [page, setPage] = useState(1);
  const [numPages, setNumPages] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);
  const [options, setOptions] = useState({id:null, priority:null, status:status, open_start:null, open_end:null});
  const [goToID, setGoToID] = useState('');
  const { markInstances } = useMark();
  const {
    handleSubmitting,
    submittingComplete,
  } = useSubmitting();

  // Update searchTerm when field input changes.
  const handleChange = event => {
    tempSearchTerm.current.value = event.target.value;
  };

  const handleIDChange = async event => {
    setGoToID(event.target.value);
  };

  // Use searchTerm to filter service_requests.
  const handleSubmit = async event => {
    event.preventDefault();
    setSearchTerm(tempSearchTerm.current.value);
    setPage(1);
  }

  const handlePrintAllClick = async (e) => {
    setIsLoading(true);

    // Fetch all service_requests data.
    let service_requests = [];
    let nextUrl = '/hotline/api/servicerequests/?page=1&search=' + searchTerm + '&incident=' + incident + '&organization=' + organization;
    do {
      const response = await axios.get(nextUrl, {
        params: {
          status: options.status,
          priority: options.priority,
          open_start: options.open_start,
          open_end: options.open_end,
        },
      })
      .catch(error => {
        if (error.response) {
          setShowSystemError(true);
        }
      });

      service_requests.push(...response.data.results);
      nextUrl = response.data.next;
      if (nextUrl) {
        nextUrl = '/hotline/' + response.data.next.split('/hotline/')[1];
      }
    } while(nextUrl != null)

    handleSubmitting()
      .then(() => printAllServiceRequests(service_requests))
      .then(submittingComplete)
      .finally(() => setIsLoading(false));
  }

  const handleGeoJsonDownload = (e) => {
    setIsLoading(true);

    handleSubmitting()
    .then(() => downloadGeoJson()
      )
    .then(submittingComplete)
    .finally(() => setIsLoading(false));
  }

  const downloadGeoJson = async () => {

    // Fetch all service_requests data.
    let service_requests = [];
    let nextUrl = '/hotline/api/servicerequests/?page=1&search=' + searchTerm + '&light=true&incident=' + incident + '&organization=' + organization;
    do {
      const response = await axios.get(nextUrl, {
        params: {
          status: options.status,
          priority: options.priority,
          open_start: options.open_start,
          open_end: options.open_end,
        },
      })
      .catch(error => {
        if (error.response) {
          setShowSystemError(true);
        }
      });

      service_requests.push(...response.data.results);
      nextUrl = response.data.next;
      if (nextUrl) {
        nextUrl = '/hotline/' + response.data.next.split('/hotline/')[1];
      }
    } while(nextUrl != null)

    var params = '';
    service_requests.forEach(sr => params = params + "id=" + sr.id + "&")
    var fileDownload = require('js-file-download');
    axios.get('/hotline/api/servicerequests/download_all/', { 
            params: {
              ids: params
            },
            responseType: 'blob',
        }).then(res => {
            fileDownload(res.data, `SRs-${moment().format(DATE_FORMAT)}` + '.geojson');
        }).catch(err => {
        })
  }

  async function pushData(){
    var params = '';
    data.service_requests.forEach(sr => params = params + "id=" + sr.id + "&")
    const response = await axios.get('/hotline/api/servicerequests/push_all/', { 
            params: {
              ids: params
            },
            responseType: 'json',
    }).catch(err => {
    })
  }

  const handleClear = () => {
    priorityRef.current.clearValue();
    statusRef.current.clearValue();
    openStartRef.current.flatpickr.clear();
    openEndRef.current.flatpickr.clear();
    setOptions({id:null, priority:null, status:null, open_start:null, open_end:null});
    setTriggerRefresh(!triggerRefresh);
  };

  // Hook handling option changes.
  useEffect(() => {
    setIsDisabled(!(options.id || options.priority || options.status || options.open_start || options.open_end));
  }, [options]);

  // Hook to initialize focus.
  useEffect(() => {
    tempSearchTerm.current.focus();
  }, []);

  // Hook for initializing data.
  useEffect(() => {
    let unmounted = false;
    let source = axios.CancelToken.source();

    const fetchServiceRequests = () => {
      setData({service_requests: [], total_count: 0, isFetching: true});
      // Fetch ServiceRequest data.
      axios.get('/hotline/api/servicerequests/?page=' + page + '&search=' + searchTerm + '&incident=' + incident + '&organization=' + organization, {
        cancelToken: source.token,
        params: {
          status: options.status,
          priority: options.priority,
          open_start: options.open_start,
          open_end: options.open_end,
        },
      })
      .then(response => {
        if (!unmounted) {
          setNumPages(Math.ceil(response.data.count / ITEMS_PER_PAGE));

          let search_state = {};
					response.data.results.forEach(service_request => {
						let species = [];
						service_request.animals.forEach(animal => {
							if (!species.includes(animal.species_string_singular)) {
								species.push(animal.species_string_singular)
							}
						});
            species.sort(function(a, b) {
              return a.localeCompare(b);
            });
						search_state[service_request.id] = {species:species, selectedSpecies:species[0]};
					});
          setData({service_requests: response.data.results, total_count: response.data.count, isFetching: false});
					setSearchState(search_state);

          // highlight search terms
          markInstances(searchTerm);
        }
      })
      .catch(error => {
        if (!unmounted) {
          setData({service_requests: [], total_count:0, isFetching: false});
          setShowSystemError(true);
        }
      });
    };

    fetchServiceRequests();

    // Cleanup.
    return () => {
      unmounted = true;
      source.cancel();
    };
  }, [searchTerm, incident, triggerRefresh, page]);

  return (
    <div className="ms-2 me-2">
      <Header>Search Service Requests</Header>
      <Form onSubmit={handleSubmit}>
        <Row>
          <Col xs="2" style={{maxWidth:"150px", marginRight:"-10px", paddingRight:"0px"}}>
            <InputGroup>
              <FormControl
                type="text"
                placeholder="ID #"
                name="searchIDTerm"
                onChange={handleIDChange}
              />
              <Button variant="outline-light" type="submit" disabled={!goToID} style={{borderRadius:"0 5px 5px 0", color:"white"}} onClick={(e) => {navigate("/" + organization + "/" + incident + "/hotline/servicerequest/" + goToID)}}>Go</Button>
            </InputGroup>
          </Col>
          <Col>
          <InputGroup>
            <FormControl
              type="text"
              placeholder="Search"
              name="searchTerm"
              onChange={handleChange}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSubmit(e);
                }
              }}
              ref={tempSearchTerm}
            />
            <Button variant="outline-light" type="submit" style={{borderRadius:"0 5px 5px 0", height:"38px", color:"white"}}>Search
              <OverlayTrigger
                key={"search-information"}
                placement="right"
                overlay={
                  <Tooltip id={`tooltip-search-information`}>
                    Searchable fields: address fields, animal names, and animal owner last names.
                  </Tooltip>
                }
              >
                <FontAwesomeIcon icon={faInfoCircle} className="ms-1 fa-move-up" size="sm" inverse />
              </OverlayTrigger>
            </Button>
           <Button variant="outline-light" className="ms-1 me-1 rounded" style={{height:"38px", color:"white"}} onClick={() => {setShowFilters(!showFilters)}}>Advanced {showFilters ? <FontAwesomeIcon icon={faChevronDoubleUp} className="fa-move-up" size="sm" /> : <FontAwesomeIcon icon={faChevronDoubleDown} className="fa-move-up" size="sm" />}</Button>
            {isLoading ? (
              <div className="d-flex" style={{width:"148px", justifyContent:"center"}}>
                <Spinner
                  {...{
                    as: 'span',
                    animation: 'border',
                    size: undefined,
                    role: 'status',
                    'aria-hidden': 'true',
                    variant: 'light',
                    style: {
                      height: '2rem',
                      width: '2rem',
                      marginBottom: '0.25rem'
                    }
                  }}
                />
              </div>
            ) : (
              <ActionsDropdown variant="outline-light" title={"Download All" + " (" + `${data.total_count}` + ")"} search={true} disabled={data.isFetching || data.service_requests.length === 0}>
                <LoadingLink onClick={handlePrintAllClick} isLoading={data.isFetching} label="Service Requests Summaries" className="d-block">
                  <FontAwesomeIcon icon={faFilePdf} inverse />
                </LoadingLink>
                <LoadingLink onClick={handleGeoJsonDownload} isLoading={data.isFetching} label="Service Requests as GeoJSON" className="d-block">
                  <FontAwesomeIcon icon={faDownload} inverse />
                </LoadingLink>
              </ActionsDropdown>
            )}
          </InputGroup>
          </Col>
        </Row>
        <Collapse in={showFilters}>
          <div>
          <Card className="border rounded d-flex mt-3" style={{width:"100%"}}>
            <Card.Body>
              <Row className="gx-2" style={{marginBottom:"-5px"}}>
                <Col xs={"4"} style={{textTransform:"capitalize"}}>
                  <Select
                    label="Status"
                    id="statusDropdown"
                    name="Status"
                    type="text"
                    placeholder="Select Status"
                    options={[{ value: 'open', label: 'Open' },{ value: 'assigned', label: 'Assigned' },{ value: 'closed', label: 'Closed' },{ value: 'canceled', label: 'Canceled' },]}
                    styles={customStyles}
                    isClearable={true}
                    ref={statusRef}
                    onChange={(instance) => {
                      setOptions({...options, status: instance ? instance.value : null});
                    }}
                  />
                </Col>
                <Col xs={4}>
                  <Select
                    label="Priority"
                    id="priorityDropdown"
                    name="priority"
                    type="text"
                    placeholder="Select Priority"
                    options={[{ value: 1, label: 'Urgent' },
                    { value: 2, label: 'High' },
                    { value: 4, label: 'Low' }]}
                    styles={customStyles}
                    isClearable={true}
                    ref={priorityRef}
                    onChange={(instance) => {
                      setOptions({...options, priority: instance ? instance.value : null})
                    }}
                  />
                </Col>
                <Col className="flex-grow-1" xs="3">
                  <Button className="btn btn-primary" style={{maxHeight:"36px", width:"100%"}} onClick={() => {tempSearchTerm.current.value !== searchTerm ? setSearchTerm(tempSearchTerm.current.value) : setTriggerRefresh(!triggerRefresh);}} disabled={isDisabled}>Apply</Button>
                </Col>
              </Row>
              <Row className="gx-2" style={{marginBottom:"0px", maxHeight:"37px"}}>
                <Col xs={4}>
                  <DateRangePicker
                    name={`start_date_range_picker`}
                    id={`start_date_range_picker`}
                    placeholder={"Opened Start Date"}
                    mode="single"
                    data-enable-time={true}
                    clearable={"true"}
                    hour={0}
                    style={{height:"36px"}}
                    ref={openStartRef}
                    onChange={(dateRange) => {
                      setOptions({...options, open_start: dateRange.length ? dateRange[0] : null})
                    }}
                  />
                </Col>
                <Col xs={4}>
                  <DateRangePicker
                    name={`end_date_range_picker`}
                    id={`end_date_range_picker`}
                    placeholder={"Opened End Date"}
                    mode="single"
                    data-enable-time={true}
                    clearable={"true"}
                    hour={23}
                    minute={59}
                    style={{height:"36px"}}
                    ref={openEndRef}
                    onChange={(dateRange) => {
                      setOptions({...options, open_end: dateRange.length ? dateRange[0] : null})
                    }}
                  />
                </Col>
                <Col className="flex-grow-1" xs="3">
                  <Button className="mb-3" variant="outline-light" style={{maxHeight:"36px", width:"100%"}} onClick={handleClear}>Clear</Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>
          </div>
        </Collapse>
      </Form>
      {/* here */}
      {data.service_requests.map((service_request, index) => (
        <div key={service_request.id} className="mt-3 border rounded">
          <div className="card-header">
            <h4 style={{marginBottom:"-2px",  marginLeft:"-12px"}}>
              <OverlayTrigger
                key={"request-details"}
                placement="top"
                overlay={
                  <Tooltip id={`tooltip-request-details`}>
                    Service request details
                  </Tooltip>
                }
              >
                <Link href={"/" + organization + "/" + incident + "/hotline/servicerequest/" + service_request.id_for_incident}><FontAwesomeIcon icon={faDotCircle} className="me-2" inverse /></Link>
              </OverlayTrigger>
              SR#{service_request.id_for_incident}
              &nbsp;-&nbsp;{service_request.full_address}
            </h4>
          </div>
          <CardGroup style={{marginBottom:"-6px"}}>
            <Card style={{marginBottom:"6px"}}>
              <Card.Body>
                <Card.Title style={{marginTop:"-9px", marginBottom:"8px", marginLeft:"0px"}} className="d-flex">
                  Information
                  {service_request.priority === 1 ?
                    <OverlayTrigger
                      key={"highest"}
                      placement="top"
                      overlay={
                        <Tooltip id={`tooltip-highest`}>
                          {priorityText[service_request.priority]} priority
                        </Tooltip>
                      }
                    >
                      <FontAwesomeIcon icon={faChevronDoubleUp} size="sm" className="ms-1 fa-move-down"/>
                    </OverlayTrigger>
                  : service_request.priority === 2 ?
                    <OverlayTrigger
                      key={"high"}
                      placement="top"
                      overlay={
                        <Tooltip id={`tooltip-high`}>
                          {priorityText[service_request.priority]} priority
                        </Tooltip>
                      }
                    >
                      <FontAwesomeIcon icon={faChevronUp} size="sm" className="ms-1 fa-move-down"/>
                    </OverlayTrigger>
                  : service_request.priority === 3 ?
                    <OverlayTrigger
                      key={"medium"}
                      placement="top"
                      overlay={
                        <Tooltip id={`tooltip-medium`}>
                          {priorityText[service_request.priority]} priority
                        </Tooltip>
                      }
                    >
                      <FontAwesomeIcon icon={faEquals} size="sm" className="ms-1 fa-move-down"/>
                    </OverlayTrigger>
                  : service_request.priority === 4 ?
                    <OverlayTrigger
                      key={"low"}
                      placement="top"
                      overlay={
                        <Tooltip id={`tooltip-low`}>
                          {priorityText[service_request.priority]} priority
                        </Tooltip>
                      }
                    >
                      <FontAwesomeIcon icon={faChevronDown} size="sm" className="ms-1 fa-move-down"/>
                    </OverlayTrigger>
                  : service_request.priority === 5 ?
                    <OverlayTrigger
                      key={"lowest"}
                      placement="top"
                      overlay={
                        <Tooltip id={`tooltip-lowest`}>
                          {priorityText[service_request.priority]} priority
                        </Tooltip>
                      }
                    >
                      <FontAwesomeIcon icon={faChevronDoubleDown} size="sm" className="ms-1 fa-move-down"/>
                    </OverlayTrigger>
                  : ""}
                  {service_request.verbal_permission ?
                  <OverlayTrigger
                    key={"verbal"}
                    placement="top"
                    overlay={
                      <Tooltip id={`tooltip-verbal`}>
                        Verbal Liability Release
                      </Tooltip>
                    }
                  >
                    <FontAwesomeIcon icon={faCommentSmile} size="sm" className="ms-1" />
                  </OverlayTrigger> : ""
                  }
                  {service_request.key_provided ?
                  <OverlayTrigger
                    key={"key"}
                    placement="top"
                    overlay={
                      <Tooltip id={`tooltip-key`}>
                        Key at staging
                      </Tooltip>
                    }
                  >
                    <FontAwesomeIcon icon={faKey} size="sm" className="ms-1" transform={'shrink-2'} />
                  </OverlayTrigger> :
                  <OverlayTrigger
                    key={"no-key"}
                    placement="top"
                    overlay={
                      <Tooltip id={`tooltip-no-key`}>
                        No key at staging
                      </Tooltip>
                    }
                  >
                    <span className="fa-layers" style={{marginLeft:"2px"}}>
                      <FontAwesomeIcon icon={faKey} size="sm" transform={'shrink-2'} />
                      <FontAwesomeIcon icon={faBan} color="#ef5151" size="sm" transform={'shrink-1'} />
                    </span>
                  </OverlayTrigger>
                  }
                  {service_request.accessible === "yes" ?
                  <OverlayTrigger
                    key={"accessible"}
                    placement="top"
                    overlay={
                      <Tooltip id={`tooltip-accessible`}>
                        Easily accessible
                      </Tooltip>
                    }
                  >
                    <span className="fa-layers me-1">
                      <FontAwesomeIcon icon={faCar} size="sm" className="ms-1 fa-move-up" />
                    </span>
                  </OverlayTrigger> : service_request.accessible === "no" ?
                  <OverlayTrigger
                    key={"not-accessible"}
                    placement="top"
                    overlay={
                      <Tooltip id={`tooltip-not-accessible`}>
                        Not easily accessible
                      </Tooltip>
                    }
                  >
                    <span className="fa-layers" style={{marginLeft:"2px"}}>
                      <FontAwesomeIcon icon={faCar} size="sm" className="fa-move-down" />
                      <FontAwesomeIcon icon={faBan} color="#ef5151" size="sm" transform={'shrink-1'} />
                    </span>
                  </OverlayTrigger>
                  :""}
                  {service_request.turn_around === "yes" ?
                  <OverlayTrigger
                    key={"turnaround"}
                    placement="top"
                    overlay={
                      <Tooltip id={`tooltip-turnaround`}>
                        Room to turn around
                      </Tooltip>
                    }
                  >
                    <FontAwesomeIcon icon={faTrailer} size="sm" className="ms-1 fa-move-down" />
                  </OverlayTrigger> : service_request.turn_around === "no" ?
                  <OverlayTrigger
                    key={"no-turnaround"}
                    placement="top"
                    overlay={
                      <Tooltip id={`tooltip-no-turnaround`}>
                        No room to turn around
                      </Tooltip>
                    }
                  >
                    <span className="fa-layers ms-1">
                      <FontAwesomeIcon icon={faTrailer} size="sm" />
                      <FontAwesomeIcon icon={faBan} color="#ef5151" size="sm" transform={'shrink-1'} />
                    </span>
                  </OverlayTrigger>
                   : ""}
                </Card.Title>
                <Scrollbar style={{height:"159px", marginBottom:"-16px"}} renderThumbHorizontal={props => <div {...props} style={{...props.style, display: 'none'}} />}>
                  <ListGroup>
                    <ListGroup.Item style={{textTransform:"capitalize"}}>
                      <Row>
                        <Col xs="4"><b>Status: </b>{service_request.status}</Col>
                        <Col xs="8" className="text-right"><b>Opened: </b><Moment format="LLL">{service_request.timestamp}</Moment></Col>
                     </Row>
                    </ListGroup.Item>
                    {service_request.owner_objects.map(owner => (
                      <ListGroup.Item key={owner.id}>
                        <b>Owner: </b><Link href={"/" + organization + "/" + incident + "/people/owner/" + owner.id} className="text-link" style={{textDecoration:"none", color:"white"}}>{owner.first_name} {owner.last_name}</Link>
                        {owner.display_phone ?
                        <OverlayTrigger
                          key={"owner-phone"}
                          placement="top"
                          overlay={
                            <Tooltip id={`tooltip-owner-phone`}>
                              Phone: {owner.display_phone}
                            </Tooltip>
                          }
                        >
                          <FontAwesomeIcon icon={faPhoneRotary} className="ms-1" inverse />
                        </OverlayTrigger>
                        : ""}
                        {owner.email ?
                        <OverlayTrigger
                          key={"owner-email"}
                          placement="top"
                          overlay={
                            <Tooltip id={`tooltip-owner-email`}>
                              Email: {owner.email}
                            </Tooltip>
                          }
                        >
                          <FontAwesomeIcon icon={faEnvelope} className="ms-1" inverse />
                        </OverlayTrigger>
                        : ""}
                      </ListGroup.Item>
                    ))}
                    {service_request.owners.length < 1 ? <ListGroup.Item><b>Owner: </b>No Owner</ListGroup.Item> : ""}
                    {service_request.reporter ? <ListGroup.Item><b>Reporter: </b><Link href={"/" + organization + "/" + incident + "/people/reporter/" + service_request.reporter} className="text-link" style={{textDecoration:"none", color:"white"}}>{service_request.reporter_object.first_name} {service_request.reporter_object.last_name}</Link></ListGroup.Item> : ""}
                  </ListGroup>
                </Scrollbar>
              </Card.Body>
            </Card>
            {searchState[service_request.id] ?
              <Card style={{marginBottom:"6px"}}>
                <Card.Body style={{width:"525px"}}>
                  <Card.Title style={{marginTop:"-10px", marginBottom:"12px"}}>
                    <Scrollbar horizontal="true" autoHide style={{height:"32px", width:"485px"}} renderView={props => <div {...props} style={{...props.style, marginBottom:"-18px", marginRight:"0px", overflowX:"auto", overflowY: "hidden"}}/>} renderThumbVertical={props => <div {...props} style={{...props.style, display: 'none'}} />}>
                      <ListGroup horizontal className="ps-0 pe-0 d-flex" style={{display:"inline-block", whiteSpace:"nowrap"}}>
                      {searchState[service_request.id].species.map(species => (
                        <ListGroup.Item key={species} active={searchState[service_request.id].selectedSpecies === species ? true : false} style={{textTransform:"capitalize", cursor:'pointer', paddingTop:"4px", paddingBottom:"4px", zIndex:"10000"}} onClick={() => setSearchState(prevState => ({ ...prevState, [service_request.id]:{...prevState[service_request.id], selectedSpecies:species} }))}>{species}</ListGroup.Item>
                      ))}
                      </ListGroup>
                    </Scrollbar>
                  </Card.Title>
                  <ListGroup style={{height:"159px", overflowY:"auto", marginTop:"-12px"}}>
                    <Scrollbar style={{height:"159px", marginBottom:"-16px"}} renderThumbHorizontal={props => <div {...props} style={{...props.style, display: 'none'}} />}>
                      {service_request.animals.filter(animal => animal.species_string_singular === searchState[service_request.id].selectedSpecies).map((animal, i) => (
                        <ListGroup.Item key={animal.id} className="rounded">
                          <b><Link href={"/" + organization + "/" + incident + "/animals/" + animal.id_for_incident} className="text-link" style={{textDecoration:"none", color:"white"}}>A#{animal.id_for_incident}</Link>:</b>&nbsp;&nbsp;{Number(animal.animal_count) === 1 ? <span>{animal.name || "Unknown Name"}</span> : <span>{animal.animal_count} {titleCase(animal.species_string)}</span>}
                          &nbsp;- {animal.status}
                        </ListGroup.Item>
                      ))}
                    {service_request.animals.length < 1 ? <ListGroup.Item>No Animals</ListGroup.Item> : ""}
                    </Scrollbar>
                  </ListGroup>
              </Card.Body>
            </Card>
            : ""}
          </CardGroup>
        </div>
      ))}
      <p className="mt-3">{data.isFetching ? 'Fetching service requests...' : <span>{data.service_requests.length ? '' : 'No service requests found.'}</span>}</p>
      <Row className="mb-3">
        <Col xs={12} className="ps-0">
          <Paginator page={page} numPages={numPages} setPage={setPage} />
        </Col>
      </Row>
    </div>
  )
}

export default ServiceRequestSearch;
